import {
    CrownOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";
import { memo, useCallback, useMemo } from "react";
import { AnyTask } from "tm-shared/types";

import { PrimaryText } from "@/components/Typography/Styled";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppSelector } from "@/hooks/redux";
import { QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";

import { StyledContainer, StyledMBCheckbox, StyledMBTaskLabel } from "./Styled";

interface TaskItemViewProps {
    task: AnyTask;
    isTaskEnabled: (task: AnyTask) => boolean;
    onTaskClick: (task: AnyTask) => void;
    isTaskDirty: boolean;
    accent?: boolean;
    status?: QueueTaskStatus | null;
}

const TaskItemViewInner = ({
    task,
    isTaskEnabled,
    onTaskClick,
    accent = false,
    status = null,
    isTaskDirty = false,
}: TaskItemViewProps) => {
    const { role, id: userId } = useAppSelector((s) => s.user);
    const { offlineMode } = useAppSelector((s) => s.offline);

    const isManagerNow = useMemo(() => isManagerRole(role), [role]);

    const isActive = useMemo(() => isTaskEnabled(task), [isTaskEnabled, task]);

    const performerId = task?.info?.realPerformer?.userId;

    const isManagerCompletion = useMemo(() => {
        if (isManagerNow) {
            return (task.completed && !performerId) || performerId === userId;
        }

        return task.completed && performerId && performerId !== userId;
    }, [isManagerNow, task.completed, performerId, userId]);

    const handleClick = useCallback(() => {
        onTaskClick(task);
    }, [onTaskClick, task]);

    const renderIndicator = useCallback(() => {
        if (!offlineMode) {
            if (status === QueueTaskStatus.Error) {
                return (
                    <PrimaryText fontSize={18} $textColor="error">
                        <InfoCircleOutlined />
                    </PrimaryText>
                );
            }

            if (status === QueueTaskStatus.Pending) {
                return (
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 18 }} spin />
                        }
                    />
                );
            }
        }

        return (
            <StyledMBCheckbox
                disabled
                checked={task.completed}
                aria-checked={task.completed}
                aria-disabled
                $isActive={isActive}
                $pending={Boolean(status)}
            />
        );
    }, [offlineMode, status, task.completed, isActive]);

    return (
        <StyledContainer $accent={accent}>
            {renderIndicator()}

            <StyledMBTaskLabel
                type="text"
                role="button"
                tabIndex={0}
                onClick={handleClick}
                $isActive={isActive}
                $isTaskDirty={isTaskDirty}
            >
                {task.name}
                {isManagerCompletion && (
                    <CrownOutlined style={{ fontSize: 20, color: "#d4af37" }} />
                )}
            </StyledMBTaskLabel>
        </StyledContainer>
    );
};

export const TaskItemView = memo(TaskItemViewInner);

TaskItemView.displayName = "TaskItemView";
